<script>
  // The app's one menu: a dropdown by its anchor on a desktop, a bottom sheet
  // on a phone (BinderCard, the sidebar, the want page's Project tiles). An
  // item is an action (`onClick`) or a link (`href`, opened in a new tab,
  // with an optional icon). Escape and a click away close it, and the arrow
  // keys (Home / End too) move between its items while it is open;
  // `focusFirst` (a menu opened from the keyboard as well as by pointer)
  // moves focus to its first item on open and gives it back on close.

  /**
   * @type {{
   *   isOpen: boolean,
   *   items: Array<{ label: string, variant?: string, onClick?: () => void, href?: string, icon?: string | null }>,
   *   title?: string,
   *   anchorElement?: HTMLElement | null,
   *   focusFirst?: boolean,
   *   onClose: () => void
   * }}
   */
  let {
    isOpen = false,
    items = [],
    title = '',
    anchorElement = null,
    focusFirst = false,
    onClose,
  } = $props();

  let menuEl = $state(null);

  // Position menu next to anchor on desktop
  let menuStyle = $state('');

  $effect(() => {
    if (isOpen && anchorElement && menuEl) {
      positionMenu();
    }
  });

  function positionMenu() {
    if (!anchorElement || !menuEl) return;

    // Only position on desktop (mobile uses bottom sheet)
    if (window.innerWidth <= 767) {
      menuStyle = '';
      return;
    }

    const anchorRect = anchorElement.getBoundingClientRect();
    const menuRect = menuEl.getBoundingClientRect();

    let top = anchorRect.bottom + 4;
    let left = anchorRect.right - menuRect.width;

    // Keep within viewport
    if (left < 8) left = 8;
    if (top + menuRect.height > window.innerHeight - 8) {
      top = anchorRect.top - menuRect.height - 4;
    }

    menuStyle = `top: ${top}px; left: ${left}px;`;
  }

  function handleOverlayClick() {
    onClose?.();
  }

  /**
   * The menu's keys, listened for on the window only while the menu is open
   * (the action lives on the open menu): Escape closes it; the arrows move
   * between its items, wrapping, and Home / End jump to the ends. An item
   * that cannot take focus (the phone-only Close on a desktop) is passed.
   * @param {HTMLElement} node
   */
  function menuKeys(node) {
    /** @param {KeyboardEvent} e */
    function onKeydown(e) {
      if (e.key === 'Escape') {
        onClose?.();
        return;
      }
      const items = /** @type {HTMLElement[]} */ ([...node.querySelectorAll('[role="menuitem"]')]);
      const at = items.indexOf(/** @type {HTMLElement} */ (document.activeElement));
      /** @type {Record<string, [number, number]>} start, step */
      const moves = {
        ArrowDown: [at + 1, 1],
        ArrowUp: [at < 0 ? items.length - 1 : at - 1, -1],
        Home: [0, 1],
        End: [items.length - 1, -1],
      };
      const move = moves[e.key];
      if (!move || !items.length) return;
      e.preventDefault();
      for (let i = 0, to = move[0]; i < items.length; i++, to += move[1]) {
        const item = items[(to + items.length) % items.length];
        item.focus();
        if (document.activeElement === item) return;
      }
    }
    window.addEventListener('keydown', onKeydown);
    return { destroy: () => window.removeEventListener('keydown', onKeydown) };
  }

  /**
   * While `enabled`: focus in on open (the first item) and back to whatever
   * had it on close. Follows `enabled` as it changes.
   * @param {HTMLElement} node
   * @param {boolean} enabled
   */
  function holdFocus(node, enabled) {
    /** @type {HTMLElement | null} */
    let before = null;
    /** @param {boolean} on */
    function hold(on) {
      if (!on) {
        before = null;
        return;
      }
      if (before) return;
      before = /** @type {HTMLElement | null} */ (document.activeElement);
      queueMicrotask(() =>
        /** @type {HTMLElement | null} */ (node.querySelector('[role="menuitem"]'))?.focus()
      );
    }
    hold(enabled);
    return {
      /** @param {boolean} on */
      update: hold,
      destroy() {
        if (before?.isConnected) before.focus();
      },
    };
  }
</script>

{#if isOpen}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <div class="context-menu-overlay" onclick={handleOverlayClick}></div>

  <div
    class="binder-context-menu visible"
    role="menu"
    aria-label={title || undefined}
    bind:this={menuEl}
    style={menuStyle}
    use:menuKeys
    use:holdFocus={focusFirst}
  >
    {#if title}
      <div class="context-menu-header">{title}</div>
    {/if}

    {#each items as item, i (i)}
      {#if item.href}
        <a
          class="context-menu-btn context-menu-btn-link"
          role="menuitem"
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          onclick={() => onClose?.()}
        >
          {#if item.icon}<img src={item.icon} alt="" />{/if}
          <span class="link-label">{item.label}</span>
          <span aria-hidden="true">&#8599;</span>
        </a>
      {:else}
        <button
          class="context-menu-btn context-menu-btn-{item.variant || 'default'}"
          role="menuitem"
          onclick={() => {
            item.onClick?.();
            onClose?.();
          }}
        >
          {item.label}
        </button>
      {/if}
    {/each}

    <button
      class="context-menu-btn context-menu-btn-cancel"
      role="menuitem"
      onclick={handleOverlayClick}
    >
      Close
    </button>
  </div>
{/if}

<style>
  /* Desktop: transparent overlay, just for click-away dismiss */
  .context-menu-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 999;
  }

  /* Mobile: visible overlay for bottom sheet */
  @media (max-width: 767px) {
    .context-menu-overlay {
      background-color: rgba(0, 0, 0, 0.4);
    }
  }

  /* Desktop: floating dropdown menu */
  .binder-context-menu {
    position: fixed;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    padding: 4px;
    z-index: 1000;
    opacity: 0;
    transform: scale(0.95);
    transition:
      opacity 0.15s ease,
      transform 0.15s ease;
    min-width: 180px;
  }

  .binder-context-menu.visible {
    opacity: 1;
    transform: scale(1);
  }

  /* Mobile: bottom sheet style */
  @media (max-width: 767px) {
    .binder-context-menu {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      top: auto;
      border-radius: 16px 16px 0 0;
      padding: 8px 16px 24px;
      min-width: auto;
      transform: translateY(100%);
      transition: transform 0.2s ease-out;
    }

    .binder-context-menu.visible {
      transform: translateY(0);
    }
  }

  /* Desktop: hide header (compact menu) */
  .context-menu-header {
    display: none;
  }

  /* Mobile: show header */
  @media (max-width: 767px) {
    .context-menu-header {
      display: block;
      font-weight: 600;
      font-size: 1rem;
      color: #374151;
      padding: 0.75rem 0.5rem;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 0.25rem;
    }
  }

  /* Desktop: compact button style */
  .context-menu-btn {
    display: block;
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: none;
    background: transparent;
    text-align: left;
    font-size: 0.875rem;
    cursor: pointer;
    border-radius: 4px;
    transition: background 0.15s;
    font-family: inherit;
    color: #374151;
  }

  .context-menu-btn:hover {
    background: #f3f4f6;
  }

  /* A link item: its site icon, the address (cut short), and the new-tab
     arrow. */
  .context-menu-btn-link {
    display: flex;
    align-items: center;
    gap: 8px;
    box-sizing: border-box;
    max-width: 320px;
    text-decoration: none;
  }
  .context-menu-btn-link img {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
  .link-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Mobile: larger touch-friendly buttons */
  @media (max-width: 767px) {
    .context-menu-btn {
      padding: 0.875rem 1rem;
      margin: 0.25rem 0;
      border-radius: 10px;
      font-size: 1rem;
      text-align: center;
    }
    .context-menu-btn-link {
      max-width: none;
      justify-content: center;
    }
    .link-label {
      flex: 0 1 auto;
    }
  }

  .context-menu-btn-warning {
    background-color: #fef3c7;
    color: #d97706;
  }

  .context-menu-btn-warning:hover {
    background-color: #fde68a;
  }

  .context-menu-btn-danger {
    background-color: #fee2e2;
    color: #dc2626;
  }

  .context-menu-btn-danger:hover {
    background-color: #fecaca;
  }

  .context-menu-btn-star {
    background-color: #fef9c3;
    color: #ca8a04;
  }

  .context-menu-btn-star:hover {
    background-color: #fef08a;
  }

  /* Desktop: hide cancel button (click away to dismiss) */
  .context-menu-btn-cancel {
    display: none;
  }

  /* Mobile: show cancel button */
  @media (max-width: 767px) {
    .context-menu-btn-cancel {
      display: block;
      background-color: #f3f4f6;
      color: #6b7280;
      margin-top: 0.5rem;
    }

    .context-menu-btn-cancel:hover {
      background-color: #e5e7eb;
    }
  }
</style>
